import React, { useState, useRef } from 'react';
import axios from 'axios';
import { hasRoleAccess } from './utils/roles';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import './GlobalStyles.css';
import { PDFDocument } from 'pdf-lib';

const DocumentUploader = ({ entryId, userRole, isMobile, refreshDocuments }) => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const canUploadDocs = hasRoleAccess(userRole, 'document-create');

  // -----------------------
  // States for File Upload
  // -----------------------
  const [files, setFiles] = useState([]);
  const [entryNotes, setEntryNotes] = useState('');

  // -----------------------
  // States for Native Photo Capture (single photo)
  // -----------------------
  const [photo, setPhoto] = useState(null); // preview URL for photo capture
  const [nativePhotoFile, setNativePhotoFile] = useState(null); // original file object
  const [photoNotes, setPhotoNotes] = useState('');
  const fileInputRef = useRef(null);

  // -----------------------
  // States for Document Scanning (multiple pages)
  // -----------------------
  const [scannedPages, setScannedPages] = useState([]);
  const [scannedDocNotes, setScannedDocNotes] = useState('');
  const [capturedScan, setCapturedScan] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [isProcessingScan, setIsProcessingScan] = useState(false);
  const cropperRef = useRef(null);
  const [scannedDocName, setScannedDocName] = useState('');
  const documentScanInputRef = useRef(null);

  // -----------------------
  // Helper: Generate default file name
  // -----------------------
  const generateDefaultDocName = () => {
    const date = new Date();
    const yy = date.getFullYear().toString().slice(-2);
    const mm = ('0' + (date.getMonth() + 1)).slice(-2);
    const dd = ('0' + date.getDate()).slice(-2);
    const hh = ('0' + date.getHours()).slice(-2);
    const min = ('0' + date.getMinutes()).slice(-2);
    const ss = ('0' + date.getSeconds()).slice(-2);
    return `doc_${yy}${mm}${dd}_${hh}${min}${ss}.pdf`;
  };

  // -----------------------
  // Traditional File Upload Handlers
  // -----------------------
  const handleFileChange = (e) => {
    setFiles([...files, ...Array.from(e.target.files)]);
  };

  const handleFileRemove = (fileName) => {
    setFiles(files.filter((f) => f.name !== fileName));
  };

  const handleNotesChange = (e) => {
    setEntryNotes(e.target.value);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    if (entryNotes.trim()) {
      formData.append('EntryNotes', entryNotes);
    }
    files.forEach((file) => {
      formData.append('File', file);
    });
    try {
      await axios.put(
        `${apiBaseUrl}/api/location-entry-documentation/${entryId}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000, // increased timeout to 60 seconds
        }
      );
      alert('File uploaded successfully');
      setEntryNotes('');
      setFiles([]);
      if (refreshDocuments) refreshDocuments();
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('File upload failed');
    }
  };

  // -----------------------
  // Native Photo Capture Handlers (for single photo)
  // -----------------------
  const handleTakePhoto = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleNativePhotoSelected = (e) => {
    const file = e.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setPhoto(previewUrl);
      setNativePhotoFile(file);
    }
  };

  // **Changed**: Now uploads as image instead of converting to PDF
  const handleSavePhoto = async () => {
    if (!nativePhotoFile) return;
    try {
      const formData = new FormData();
      formData.append('File', nativePhotoFile, nativePhotoFile.name || 'captured_photo.jpg');
      formData.append('EntryNotes', photoNotes);

      await axios.put(
        `${apiBaseUrl}/api/location-entry-documentation/${entryId}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000, // increased timeout to 60 seconds
        }
      );

      alert('Photo uploaded successfully');
      setPhoto(null);
      setNativePhotoFile(null);
      setPhotoNotes('');
      if (refreshDocuments) refreshDocuments();
    } catch (error) {
      console.error('Error uploading captured photo:', error);
      alert('Failed to upload captured photo');
    }
  };

  // -----------------------
  // Document Scan Handlers (using native camera)
  // -----------------------
  const handleTakeScanDocument = () => {
    setScannedDocName(generateDefaultDocName());
    if (documentScanInputRef.current) {
      documentScanInputRef.current.value = null;
      documentScanInputRef.current.click();
    }
  };

  const handleNativeScanSelected = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setCapturedScan(previewUrl);
      setShowCropper(true);

      // Start contrast enhancement automatically (show a spinner meanwhile)
      setIsProcessingScan(true);
      try {
        const enhanced = await applyContrastEnhancement(previewUrl, 150);
        setCapturedScan(enhanced);
      } catch (error) {
        console.error('Contrast enhancement failed:', error);
      }
      setIsProcessingScan(false);
    }
  };

  // -----------------------
  // Contrast Enhancement using Canvas
  // -----------------------
  const applyContrastEnhancement = (imageDataUrl, contrast = 150) => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'Anonymous';
      image.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = image.width;
        canvas.height = image.height;
        const ctx = canvas.getContext('2d');
        ctx.filter = `contrast(${contrast}%)`;
        ctx.drawImage(image, 0, 0, image.width, image.height);
        resolve(canvas.toDataURL('image/jpeg', 1));
      };
      image.onerror = reject;
      image.src = imageDataUrl;
    });
  };

  // -----------------------
  // Cropper Handlers for Document Scan
  // -----------------------
  const handleAddAnotherPageFromCropper = () => {
    if (cropperRef.current) {
      const croppedDataUrl = cropperRef.current.cropper
        .getCroppedCanvas()
        .toDataURL('image/jpeg', 1);
      setScannedPages((prev) => [...prev, croppedDataUrl]);
      setShowCropper(false);
      setCapturedScan(null);
      if (documentScanInputRef.current) {
        documentScanInputRef.current.value = null;
        documentScanInputRef.current.click();
      }
    }
  };

  const handleDoneCropping = () => {
    if (cropperRef.current) {
      const croppedDataUrl = cropperRef.current.cropper
        .getCroppedCanvas()
        .toDataURL('image/jpeg', 1);
      setScannedPages((prev) => [...prev, croppedDataUrl]);
    }
    setShowCropper(false);
    setCapturedScan(null);
  };

  // -----------------------
  // Final Upload for All Scanned Pages
  // -----------------------
  const handleUploadScannedDocument = async () => {
    if (scannedPages.length === 0) {
      alert('No scanned pages to upload.');
      return;
    }

    try {
      const pdfDoc = await PDFDocument.create();
      for (let pageDataUrl of scannedPages) {
        const imageBytes = await fetch(pageDataUrl).then((res) => res.arrayBuffer());
        const jpgImage = await pdfDoc.embedJpg(imageBytes);
        const { width, height } = jpgImage.scale(1);
        const pdfPage = pdfDoc.addPage([width, height]);
        pdfPage.drawImage(jpgImage, { x: 0, y: 0, width, height });
      }
      const pdfBytes = await pdfDoc.save();
      const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });

      let fileName = scannedDocName || generateDefaultDocName();
      if (!fileName.toLowerCase().endsWith('.pdf')) {
        fileName += '.pdf';
      }
      const formData = new FormData();
      formData.append('File', pdfBlob, fileName);
      formData.append('EntryNotes', scannedDocNotes);

      await axios.put(
        `${apiBaseUrl}/api/location-entry-documentation/${entryId}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000, // increased timeout to 60 seconds
        }
      );

      alert('Scanned document uploaded successfully as ' + fileName);
      setScannedPages([]);
      setScannedDocNotes('');
      setScannedDocName('');
      if (refreshDocuments) refreshDocuments();
    } catch (error) {
      console.error('Error uploading scanned document:', error);
      alert('Failed to upload scanned document');
    }
  };

  if (!canUploadDocs) return null;

  return (
    <div className="global-container">
      <div className="document-uploader">
        {/* Hidden file input for native photo capture */}
        <input
          type="file"
          accept="image/*"
          capture="environment"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleNativePhotoSelected}
        />

        {/* Hidden file input for native document scanning */}
        <input
          type="file"
          accept="image/*"
          capture="environment"
          ref={documentScanInputRef}
          style={{ display: 'none' }}
          onChange={handleNativeScanSelected}
        />

        {/* Cropper Modal for Scanned Document */}
        {showCropper && (
          <div className="scanner-modal">
            {isProcessingScan ? (
              <div className="loading-spinner">Processing...</div>
            ) : (
              <Cropper
                src={capturedScan}
                style={{ height: 400, width: '100%' }}
                guides={true}
                ref={cropperRef}
                viewMode={1}
                initialAspectRatio={8.5 / 11}
              />
            )}
            <div style={{ marginTop: '10px' }}>
              <button onClick={handleAddAnotherPageFromCropper} className="global-upload-button">
                Add another page
              </button>
              <button onClick={handleDoneCropping} className="global-upload-button">
                Done Cropping
              </button>
              <button
                onClick={() => {
                  setShowCropper(false);
                  setCapturedScan(null);
                }}
                className="overlay-button close-button"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Main UI */}
        <div className="capture-section">
          <h2>Document Upload Options</h2>
          {/* Conditionally render these buttons only on mobile (screen width <= 770px) */}
          {isMobile && !showCropper && (
            <div className="capture-buttons">
              <button onClick={handleTakePhoto} className="global-upload-button">
                Take Photo
              </button>
              <button onClick={handleTakeScanDocument} className="global-upload-button">
                Scan Document
              </button>
            </div>
          )}

          {/* Photo Preview */}
          {photo && (
            <div className="capture-preview">
              <img src={photo} alt="Captured Photo" />
              <button
                onClick={() => {
                  setPhoto(null);
                  setNativePhotoFile(null);
                }}
                className="delete-button retake-button"
              >
                Retake Photo
              </button>
              <div className="capture-notes-section">
                <label htmlFor="photoNotes">Photo Notes:</label>
                <textarea
                  id="photoNotes"
                  value={photoNotes}
                  onChange={(e) => setPhotoNotes(e.target.value)}
                  placeholder="Enter notes about this photo..."
                />
                <button onClick={handleSavePhoto} className="global-save-button">
                  Save Photo
                </button>
              </div>
            </div>
          )}

          {/* Scanned Pages Preview in Grid */}
          {scannedPages.length > 0 && (
            <div className="capture-preview">
              <div
                className="scanned-pages-preview"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                  gap: '10px',
                }}
              >
                {scannedPages.map((page, index) => (
                  <img
                    key={index}
                    src={page}
                    alt={`Scanned Page ${index + 1}`}
                    style={{
                      width: '100%',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      padding: '5px',
                    }}
                  />
                ))}
              </div>
              <div className="capture-notes-section">
                <label htmlFor="scannedDocNotes">Scan Notes:</label>
                <textarea
                  id="scannedDocNotes"
                  value={scannedDocNotes}
                  onChange={(e) => setScannedDocNotes(e.target.value)}
                  placeholder="Enter notes about this scanned document..."
                />
                <label htmlFor="scannedDocName">Document Name:</label>
                <input
                  id="scannedDocName"
                  type="text"
                  value={scannedDocName}
                  onChange={(e) => setScannedDocName(e.target.value)}
                  placeholder="Enter document name"
                />
                <button onClick={handleUploadScannedDocument} className="global-save-button">
                  Save and Done
                </button>
                <button onClick={() => setScannedPages([])} className="delete-button">
                  Clear All Pages
                </button>
              </div>
            </div>
          )}
        </div>

        {/* File Upload Section */}
        {!(isMobile && !showCropper) && (
          <div className="upload-section">
            <h2>Upload Document</h2>
            <form onSubmit={handleUpload} className="upload-form">
              <div className="form-group">
                <label>Entry Notes</label>
                <textarea name="EntryNotes" value={entryNotes} onChange={handleNotesChange} />
              </div>
              <div className="form-group">
                <label>Files</label>
                <input type="file" multiple onChange={handleFileChange} required />
                {files.length > 0 && (
                  <div className="file-list">
                    <p>Selected Files:</p>
                    <ul>
                      {files.map((file, index) => (
                        <li key={index}>
                          {file.name}
                          <button type="button" onClick={() => handleFileRemove(file.name)}>
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <button type="submit" className="global-upload-button">
                Upload File
              </button>
            </form>
          </div>
        )}

      </div>
    </div>
  );
};

export default DocumentUploader;
